/* ═══════════════════════════════════════════════════════════════════════
   The business's own screen.

   What a plumber opens on their phone at the end of a day, or an
   accountant leaves in a tab. It answers three questions in the order they
   are actually asked:

     1. what needs me?
     2. where's the link I give people?
     3. what did crAIg get wrong, and how do I fix it?

   Everything else is secondary and lives further down the page. In
   particular there is no chart, no engagement metric and no activity feed —
   nobody running a trade has ever wanted one, and a dashboard that opens on
   a graph is a dashboard that has forgotten who it is for.
   ═══════════════════════════════════════════════════════════════════════ */

import { useCallback, useEffect, useState } from "react";
import { applyAccent, applyTheme, prefersDark, type Theme } from "./theme";
import { CraigFace } from "./components/CraigFace";
import { DashboardLink } from "./components/chrome";
import { LetCraigDoIt, Wordmark } from "./components/Brand";

interface Overview {
  business: { slug: string; displayName: string; industry: string; branding: { accent?: string } };
  customerLink: string;
  published: boolean;
  plan: string;
  email: { address: string; verified: boolean };
  /* The ACCOUNT'S credit, shown on every page of theirs. One pot: see the
     comment on Credit below. */
  credit: {
    balanceMicros: number;
    spentThisMonthMicros: number;
    toppedUpThisMonthMicros: number;
    conversationsThisMonth: number;
    perCaseMicros: number;
  };
  /** What THIS page has spent out of it, this month. */
  thisPage: { spentThisMonthMicros: number; conversationsThisMonth: number };
  cases: {
    thisMonth: number;
    waitingOnYou: number;
    waitingOnCustomer: number;
    inProgress: number;
    done: number;
  };
}

interface InboxItem {
  id: string;
  kind: string;
  title: string;
  body: string | null;
  delivered: boolean;
  at: string;
  read: boolean;
  open: string | null;
}

interface CaseRow {
  id: string;
  status: string;
  who: string;
  service: string | null;
  urgency: string | null;
  declined: boolean;
  signed: boolean;
  updatedAt: string;
  open: string;
}

export function Dashboard({ page }: { page: string }) {
  /* Which page this is. An account holds several, so the screen is told
     rather than assuming — see Pages.tsx. */

  const [theme, setTheme] = useState<Theme>(() => (prefersDark() ? "dark" : "light"));
  const [overview, setOverview] = useState<Overview | null>(null);
  const [cases, setCases] = useState<CaseRow[]>([]);
  const [inbox, setInbox] = useState<{ unread: number; items: InboxItem[] }>({ unread: 0, items: [] });
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [publishing, setPublishing] = useState(false);

  useEffect(() => {
    applyTheme(theme);
    // Their own colour, so the page they manage and the page they own look
    // like the same business.
    applyAccent(overview?.business.branding.accent, theme);
  }, [theme, overview]);

  const load = useCallback(() => {
    Promise.all([
      fetch(`/api/dash/${page}/overview`).then((r) => (r.ok ? r.json() : Promise.reject(r.status))),
      fetch(`/api/dash/${page}/cases`).then((r) => (r.ok ? r.json() : Promise.reject(r.status))),
      fetch(`/api/dash/${page}/inbox`).then((r) => (r.ok ? r.json() : Promise.reject(r.status))),
    ])
      .then(([o, c, i]) => {
        setOverview(o);
        setCases(c.cases ?? []);
        setInbox(i);
      })
      .catch((status) => {
        // Not signed in is not an error worth a message — it's a redirect.
        if (status === 401) window.location.href = "/account/sign-in";
        else setError("Couldn't load that just now.");
      });
  }, []);

  useEffect(load, [load]);

  const publish = async (published: boolean) => {
    setPublishing(true);
    try {
      const res = await fetch(`/api/dash/${page}/publish`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ published }),
      });
      const body = await res.json();
      if (!res.ok) setError(body.error ?? "Couldn't change that.");
      else load();
    } finally {
      setPublishing(false);
    }
  };

  if (error) return <Centre>{error}</Centre>;
  if (!overview) return <Centre>Loading…</Centre>;

  const o = overview;

  return (
    <div className="min-h-screen flex flex-col">
      <header style={{ borderBottom: "1px solid var(--line)", background: "var(--surface)" }}>
        <div className="max-w-4xl mx-auto px-5 py-3.5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            {/* One page up. An account can hold several chats and this
                screen is only one of them; the inbox, the balance and the
                rest of the chats all live at /dashboard. Without this the
                only way back is the browser's own button, which isn't
                there for a bookmark or a link opened in a new tab. */}
            <DashboardLink />
            <CraigFace size={24} />
            <span className="mono truncate" style={{ fontWeight: 650, fontSize: 14.5 }}>
              {o.business.displayName}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="btn btn-quiet btn-term"
              onClick={() => setTheme((t) => (t === "dark" ? "light" : "dark"))}
              aria-label="Switch theme"
              style={{ padding: "0.3rem 0.55rem" }}
            >
              {theme === "dark" ? "☀" : "☾"}
            </button>
            <button
              type="button"
              className="btn btn-quiet btn-term"
              onClick={async () => {
                await fetch("/api/account/sign-out", { method: "POST" });
                window.location.href = "/account/sign-in";
              }}
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 w-full max-w-4xl mx-auto px-5 py-6 space-y-4">
        {/* ── Is it live, and what's the link ────────────────────────── */}
        <section className="panel panel-ticks px-5 py-5">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="term-label">Your customer link</div>
              <div className="mono" style={{ fontSize: 16, fontWeight: 650, marginTop: 4 }}>
                {o.customerLink.replace(/^https?:\/\//, "")}
              </div>
            </div>
            <span
              className="mono"
              style={{
                fontSize: 11.5,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                padding: "0.25rem 0.5rem",
                borderRadius: 3,
                border: "1px solid",
                borderColor: o.published ? "var(--ok)" : "var(--line-2)",
                color: o.published ? "var(--ok)" : "var(--ink-3)",
              }}
            >
              {o.published ? "live" : "private"}
            </span>
          </div>

          <p style={{ fontSize: 13.5, color: "var(--ink-2)", marginTop: 10 }}>
            {o.published
              ? "Put this on your website, in your text replies, on the van. Anyone who opens it is talking to crAIg."
              : "Only you can open this while it's private. Have a look, change anything that's wrong, then put it live when you're happy."}
          </p>

          <div className="flex flex-wrap gap-2 mt-3">
            <button
              type="button"
              className="btn btn-quiet btn-term"
              onClick={() => {
                void navigator.clipboard.writeText(o.customerLink);
                setCopied(true);
                setTimeout(() => setCopied(false), 1800);
              }}
            >
              {copied ? "Copied" : "Copy link"}
            </button>
            <a href={o.customerLink} target="_blank" rel="noreferrer" className="btn btn-quiet btn-term">
              {o.published ? "Open it" : "Preview it"}
            </a>
            <button
              type="button"
              className={o.published ? "btn btn-quiet btn-term" : "btn btn-primary btn-term"}
              disabled={publishing}
              onClick={() => publish(!o.published)}
            >
              {o.published ? "Take it private" : "Put it live"}
            </button>
          </div>
        </section>

        {/* ── Confirm your email ─────────────────────────────────────── */}
        {!o.email.verified && <Verify email={o.email.address} />}

        {/* ── What's happened ────────────────────────────────────────── */}
        <Inbox
          inbox={inbox}
          onRead={async (id) => {
            await fetch(`/api/dash/${page}/inbox/read`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(id ? { id } : {}),
            });
            load();
          }}
        />

        {/* ── Credit ─────────────────────────────────────────────────── */}
        <Credit credit={o.credit} thisPage={o.thisPage} published={o.published} />

        {/* ── What needs a person ────────────────────────────────────── */}
        <section className="grid gap-3 sm:grid-cols-4">
          <Stat n={o.cases.waitingOnYou} label="Need you" accent />
          <Stat n={o.cases.waitingOnCustomer} label="With the customer" />
          <Stat n={o.cases.inProgress} label="Still talking" />
          <Stat n={o.cases.thisMonth} label="This month" />
        </section>

        {/* ── The inbox ──────────────────────────────────────────────── */}
        <section className="panel px-5 py-5">
          <div className="term-label mb-3">Jobs</div>

          {cases.length === 0 ? (
            <p style={{ fontSize: 14, color: "var(--ink-2)" }}>
              Nothing yet. Once someone opens your link and talks to crAIg, it appears here.
            </p>
          ) : (
            <div className="space-y-1.5">
              {cases.map((c) => (
                <a
                  key={c.id}
                  href={c.open}
                  className="panel px-3.5 py-3 flex items-center gap-3"
                  style={{ textDecoration: "none", color: "inherit" }}
                >
                  <StatusDot status={c.status} urgent={c.urgency === "now"} />
                  <span style={{ fontWeight: 600, minWidth: 0, flex: 1 }}>{c.who}</span>
                  {c.service && (
                    <span className="mono" style={{ fontSize: 12, color: "var(--ink-3)" }}>
                      {c.service}
                    </span>
                  )}
                  <span className="mono" style={{ fontSize: 11.5, color: "var(--ink-3)" }}>
                    {label(c)}
                  </span>
                </a>
              ))}
            </div>
          )}
        </section>

        {/* ── Fixing what crAIg got wrong ────────────────────────────── */}
        <section className="panel px-5 py-5">
          <div className="term-label mb-2">What crAIg knows</div>
          <p style={{ fontSize: 14, color: "var(--ink-2)" }}>
            His prices, your hours, who you can't help, what you need people to send. Change any of
            it and it takes effect on the next conversation.
          </p>
          {/*
            Signing up no longer fills any of this in, so a new business
            arrives here with crAIg knowing nothing beyond his industry.
            Offering the interview is the fastest way out of that — and it
            reads as help rather than homework, which is exactly what it
            was not when it stood between them and an account.
          */}
          <p style={{ fontSize: 14, color: "var(--ink-2)", marginTop: 8 }}>
            Rather talk than type? crAIg will ask you about the business and fill this in himself.
          </p>
          <a
            href="/start"
            className="btn btn-primary btn-term"
            style={{ marginTop: 10, display: "inline-block" }}
          >
            Let crAIg interview me
          </a>
          <div className="flex flex-wrap gap-2" style={{ marginTop: 12 }}>
            <a href={`/dashboard/${page}/page`} className="btn btn-quiet btn-term">
              Your page and colours
            </a>
            <a href={`/dashboard/${page}/setup`} className="btn btn-quiet btn-term">
              Change what he asks
            </a>
          </div>
        </section>

        {/* ── Deleting it ────────────────────────────────────────────── */}
        <DangerZone slug={o.business.slug} name={o.business.displayName} page={page} />
      </main>

      <LetCraigDoIt />
    </div>
  );
}

/* ── Deleting a chat ────────────────────────────────────────────────────
   Last on the page, quiet until opened, and it makes you type the address.

   A one-click delete on a screen somebody checks from a van is a delete
   that eventually happens by thumb. Typing the address is three seconds
   and it is the difference between meaning it and brushing it.

   It also says exactly what goes, with the real number in front of them
   rather than "this cannot be undone" — which everyone has learned to
   click past. */

function DangerZone({ slug, name, page }: { slug: string; name: string; page: string }) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) {
    return (
      <section className="panel px-5 py-4">
        <button
          type="button"
          onClick={() => setOpen(true)}
          style={{
            background: "none",
            border: "none",
            padding: 0,
            font: "inherit",
            fontSize: 13,
            color: "var(--ink-3)",
            textDecoration: "underline",
            cursor: "pointer",
          }}
        >
          Delete this chat
        </button>
      </section>
    );
  }

  return (
    <section
      className="panel px-5 py-5"
      style={{ background: "var(--warn-tint)", borderColor: "var(--danger)" }}
    >
      <div className="term-label">Delete {name}</div>
      <p style={{ fontSize: 14, color: "var(--ink-2)", marginTop: 8, lineHeight: 1.55 }}>
        This removes the page at <strong>letcraig.ai/{slug}</strong>, every job on it, and every
        document your customers sent. Anyone with the link gets nothing. There is no undo and we
        keep no copy.
      </p>
      {/* Said out loud, because the opposite used to be true and somebody
          who has been bitten by it will not assume otherwise. */}
      <p style={{ fontSize: 13.5, color: "var(--ink-2)", marginTop: 8, lineHeight: 1.55 }}>
        Your credit stays with your account — it isn't tied to this chat, and your other chats
        keep using it.
      </p>

      <label className="block mt-3" style={{ maxWidth: 320 }}>
        <span className="term-label" style={{ display: "block", marginBottom: 4 }}>
          Type <span className="mono">{slug}</span> to confirm
        </span>
        <input
          className="field mono"
          autoFocus
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
        />
      </label>

      {error && (
        <p role="alert" style={{ fontSize: 13.5, color: "var(--danger)", marginTop: 8 }}>
          {error}
        </p>
      )}

      <div className="flex flex-wrap gap-2 mt-3">
        <button
          type="button"
          className="btn btn-primary btn-term"
          disabled={busy || typed.trim().toLowerCase() !== slug}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              const res = await fetch(`/api/dash/${page}`, {
                method: "DELETE",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ confirm: typed.trim() }),
              });
              const body = (await res.json().catch(() => ({}))) as { error?: string };
              if (!res.ok) return setError(body.error ?? "That didn't work.");
              window.location.href = "/dashboard";
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? "Deleting…" : "Delete it"}
        </button>
        <button
          type="button"
          className="btn btn-quiet btn-term"
          onClick={() => {
            setOpen(false);
            setTyped("");
            setError(null);
          }}
        >
          Keep it
        </button>
      </div>
    </section>
  );
}

/* ── Confirming an email ────────────────────────────────────────────────
   Shown until it's done, and worded as the one thing it actually blocks.

   "Verify your email" tells somebody nothing about why they should care,
   so this says what it costs them: their page cannot go public. Everything
   else — building the page, changing colours, talking to crAIg, watching
   a preview — works untouched, which is what makes putting the gate here
   rather than on signup reasonable. */

function Verify({ email }: { email: string }) {
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  return (
    <section
      className="panel px-5 py-4"
      style={{ background: "var(--warn-tint)", borderColor: "var(--warn)" }}
    >
      <div className="term-label">One thing outstanding</div>
      <p style={{ fontSize: 14, color: "var(--ink-2)", marginTop: 6, lineHeight: 1.55 }}>
        We've sent a link to <strong>{email}</strong>. Click it and you can put your page live.
        Until then everything here works, but your link stays private.
      </p>
      <button
        type="button"
        className="btn btn-quiet btn-term"
        style={{ marginTop: 10 }}
        disabled={busy || sent}
        onClick={async () => {
          setBusy(true);
          try {
            await fetch("/api/account/verify/send", { method: "POST" });
            setSent(true);
          } finally {
            setBusy(false);
          }
        }}
      >
        {sent ? "Sent — have a look" : busy ? "Sending…" : "Send it again"}
      </button>
    </section>
  );
}

/* ── The inbox ──────────────────────────────────────────────────────────
   Everything we've told them, whether or not the email arrived.

   That last part is the point. This is built from the send log, so it
   shows the EVENT rather than the message — a business whose address
   bounced, or who turned notifications off, still sees every case that
   came in. An inbox that only worked when email worked would fall silent
   at exactly the moment it mattered.

   Which is why a failed send is shown rather than hidden. "We couldn't
   reach you" is the most useful thing on this screen and the one thing an
   email could never tell them. */

function Inbox({
  inbox,
  onRead,
}: {
  inbox: { unread: number; items: InboxItem[] };
  onRead(id?: string): void;
}) {
  const [open, setOpen] = useState(false);
  // Two is enough to see there's something; the rest on request. A
  // dashboard that opens on twenty notifications is a dashboard nobody
  // reads twice.
  const shown = open ? inbox.items : inbox.items.slice(0, 3);

  if (!inbox.items.length) return null;

  return (
    <section className="panel px-5 py-5">
      <div className="flex items-center justify-between gap-3">
        <div className="term-label">
          What's happened
          {inbox.unread > 0 && (
            <span
              className="mono"
              style={{
                marginLeft: 8,
                background: "var(--accent)",
                color: "var(--accent-ink)",
                borderRadius: 999,
                padding: "0.1rem 0.45rem",
                fontSize: 11,
              }}
            >
              {inbox.unread} new
            </span>
          )}
        </div>
        {inbox.unread > 0 && (
          <button
            type="button"
            className="btn btn-quiet btn-term"
            style={{ padding: "0.25rem 0.6rem", fontSize: 12.5 }}
            onClick={() => onRead()}
          >
            Mark all read
          </button>
        )}
      </div>

      <div className="mt-3 space-y-1.5">
        {shown.map((n) => (
          <div
            key={n.id}
            className="panel px-3.5 py-3"
            style={{ borderLeft: n.read ? undefined : "3px solid var(--accent)" }}
          >
            <div className="flex items-start justify-between gap-3">
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: n.read ? 550 : 650, fontSize: 14.5 }}>
                  {n.body || n.title}
                </div>
                <div style={{ fontSize: 12.5, color: "var(--ink-3)", marginTop: 2 }}>
                  {when(n.at)}
                  {!n.delivered && (
                    <span style={{ color: "var(--danger)" }}>
                      {" · we couldn't email you this one"}
                    </span>
                  )}
                </div>
              </div>
              {n.open && (
                <a
                  href={n.open}
                  className="btn btn-quiet btn-term"
                  style={{ padding: "0.25rem 0.6rem", fontSize: 12.5, flexShrink: 0 }}
                  onClick={() => !n.read && onRead(n.id)}
                >
                  Open
                </a>
              )}
            </div>
          </div>
        ))}
      </div>

      {inbox.items.length > 3 && (
        <button
          type="button"
          className="btn btn-quiet btn-term mt-2.5"
          style={{ padding: "0.3rem 0.7rem", fontSize: 13 }}
          onClick={() => setOpen((o) => !o)}
        >
          {open ? "Show less" : `Show all ${inbox.items.length}`}
        </button>
      )}
    </section>
  );
}

/** Relative for the recent, plain for the rest. Nobody wants an ISO string. */
function when(at: string): string {
  const mins = Math.round((Date.now() - new Date(at).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} minute${mins === 1 ? "" : "s"} ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? "" : "s"} ago`;
  return new Date(at).toLocaleDateString();
}

/* ── Credit ─────────────────────────────────────────────────────────────
   In dollars, never in tokens.

   A plumber has no idea what a token is and no reason to acquire one, and
   a balance nobody can interpret is a balance nobody tops up — which ends
   with their intake quietly refusing customers. So: what's left, what a
   conversation costs them, and roughly how many more that buys.

   The last of those is the only number that answers the question people
   actually have, which is "am I about to run out?". */

const money = (micros: number): string => {
  const d = micros / 1_000_000;
  if (d === 0) return "$0";
  return d < 1 ? `$${d.toFixed(2)}` : `$${d.toFixed(2)}`;
};

/**
 * The money, on a page that is one of possibly several.
 *
 * The balance is the ACCOUNT'S and is deliberately labelled as such. It
 * used to be the page's, which meant a second chat came with a second lot
 * of free credit and deleting a chat deleted the money — and the screen
 * saying "credit left" under one business's name is a large part of why
 * that felt right for as long as it did.
 *
 * So: their balance, said plainly, next to what THIS page has spent out of
 * it. The second number is the honest per-page one.
 */
function Credit({
  credit,
  thisPage,
  published,
}: {
  credit: Overview["credit"];
  thisPage: Overview["thisPage"];
  published: boolean;
}) {
  const { balanceMicros, perCaseMicros, spentThisMonthMicros } = credit;
  const left = perCaseMicros > 0 ? Math.floor(balanceMicros / perCaseMicros) : null;
  // Only worth distinguishing when this page isn't the whole story.
  const shared = spentThisMonthMicros !== thisPage.spentThisMonthMicros;

  // Warn while there is still time to act, not once the doors have shut.
  const empty = balanceMicros <= 0;
  const low = !empty && left !== null && left < 5;

  return (
    <section
      className="panel panel-ticks px-5 py-5"
      style={
        empty
          ? { background: "var(--warn-tint)", borderColor: "var(--danger)" }
          : low
            ? { background: "var(--warn-tint)", borderColor: "var(--warn)" }
            : undefined
      }
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="term-label">Your credit</div>
          <div className="mono" style={{ fontSize: 26, fontWeight: 700, lineHeight: 1.1, marginTop: 4 }}>
            {money(Math.max(0, balanceMicros))}
          </div>
          {shared && (
            <div style={{ fontSize: 12.5, color: "var(--ink-3)", marginTop: 2 }}>
              shared with your other chats
            </div>
          )}
        </div>
        <div style={{ textAlign: "right" }}>
          <div className="term-label">This page, this month</div>
          <div style={{ fontSize: 14, marginTop: 4 }}>
            {thisPage.conversationsThisMonth} conversation
            {thisPage.conversationsThisMonth === 1 ? "" : "s"} ·{" "}
            {money(thisPage.spentThisMonthMicros)}
          </div>
          {shared && (
            <div style={{ fontSize: 12.5, color: "var(--ink-3)", marginTop: 2 }}>
              {money(spentThisMonthMicros)} across all of them
            </div>
          )}
        </div>
      </div>

      <p style={{ fontSize: 13.5, color: "var(--ink-2)", marginTop: 12, lineHeight: 1.55 }}>
        {empty ? (
          published ? (
            <>
              <strong>Your link has stopped taking new enquiries.</strong> Anyone opening it is asked
              to contact you directly. Top up and it starts again straight away — nothing has been
              lost.
            </>
          ) : (
            <>You'll need some credit before your link can go live.</>
          )
        ) : low ? (
          <>
            <strong>Running low.</strong> About {left} more conversation{left === 1 ? "" : "s"} at
            what yours have been costing. Top up before it runs out and your customers never notice.
          </>
        ) : left !== null ? (
          <>
            Roughly {left} more conversation{left === 1 ? "" : "s"} at{" "}
            {money(perCaseMicros)} each — what yours have actually cost, not an estimate. You only
            pay for conversations that happen.
          </>
        ) : (
          <>You only pay for conversations that happen. A quiet month costs you nothing.</>
        )}
      </p>

      <div className="flex flex-wrap gap-2 mt-3">
        <button type="button" className={empty || low ? "btn btn-primary btn-term" : "btn btn-quiet btn-term"} disabled>
          Top up
        </button>
        <span style={{ fontSize: 12.5, color: "var(--ink-3)", alignSelf: "center" }}>
          Card payments aren't switched on yet — get in touch and we'll add credit by hand.
        </span>
      </div>
    </section>
  );
}

/* ── Furniture ──────────────────────────────────────────────────────────── */

/** What to say about a case in four words or fewer. */
function label(c: CaseRow): string {
  if (c.declined) return "turned away";
  if (c.urgency === "now") return "URGENT";
  if (c.status === "escalated") return "wants a person";
  if (c.status === "signed") return "ready for you";
  if (c.status === "awaiting_client") return "waiting on them";
  if (c.status === "accepted") return "you've got it";
  if (c.status === "closed") return "done";
  return "still talking";
}

function StatusDot({ status, urgent }: { status: string; urgent: boolean }) {
  const colour = urgent
    ? "var(--danger)"
    : status === "escalated" || status === "signed"
      ? "var(--accent)"
      : status === "closed" || status === "accepted"
        ? "var(--ok)"
        : "var(--line-2)";
  return (
    <span
      aria-hidden="true"
      style={{ width: 7, height: 7, borderRadius: 2, background: colour, flexShrink: 0 }}
    />
  );
}

function Stat({ n, label, accent }: { n: number; label: string; accent?: boolean }) {
  return (
    <div className="panel px-4 py-3.5">
      <div
        className="mono"
        style={{
          fontSize: 26,
          fontWeight: 700,
          lineHeight: 1,
          color: accent && n > 0 ? "var(--accent)" : "var(--ink)",
        }}
      >
        {n}
      </div>
      <div className="term-label" style={{ marginTop: 6 }}>
        {label}
      </div>
    </div>
  );
}

function Centre({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen grid place-items-center px-5">
      <div className="flex flex-col items-center gap-3">
        <CraigFace size={40} thinking />
        <span style={{ fontSize: 14, color: "var(--ink-3)" }}>{children}</span>
        <Wordmark size={12} muted />
      </div>
    </div>
  );
}
